import { useEffect, useState, useRef } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { matchApi, internApi } from "@/api";
import { EventResolvingVideo } from "@/pages/EventsResolving/EventResolvingVideo";
import { toast } from "react-toastify";
import Preloader from "@/components/Elements/Preloader";
import { EventButton } from "@/components/Elements/EventButton";
import { SelectedEvents } from "@/pages/EventsResolving/SelectedEvents/SelectedEvents";
import { StyledSubmit } from "@/pages/EventsResolving/SelectedEvents/StyledComponents";
import Switch from "@/components/Elements/switch";

const EventsResolving = () => {
  const videoRef = useRef(null);

  const [task, setTask] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState([]);
  const [selectedActiveCandidates, setSelectedActiveCandidates] = useState([]);
  const [selectedPassiveCandidates, setSelectedPassiveCandidates] = useState(
    []
  );
  const [allSelectedBoxes, setAllSelectedBoxes] = useState([]);
  const [startEventTime, setStartEventTime] = useState(0);
  const [endEventTime, setEndEventTime] = useState(0);
  const [pipeConflictId, setPipeConflictId] = useState(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [enabled, setEnabled] = useState(false); // possibility to labeling
  const [showBoxes, setShowBoxes] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const keyDownHandler = async (e) => {
      const { code, shiftKey } = e;

      if (code === "Space") {
        e.preventDefault();
      }

      if (code === "Enter") {
        submitTaskResolve();
      }

      if (!enabled) return;

      if (code === "KeyS") {
        handleStartEventTime();
      } else if (code === "KeyG") {
        handleEndEventTime();
      }

      task.buttons.forEach((label) => {
        const key = "Key" + label.split("(")[1].charAt(0).toUpperCase();
        if (code === key) {
          onSelectedEvent(label);
        }
      });
      if (code.includes("Digit")) {
        const candidateId = code.split("Digit")[1];
        // Call your specific function here with the number key
        if (shiftKey) {
          if (!task.candidates_passive?.length) return;
          onPassiveCandidateClick(Number(candidateId));
        } else {
          if (!task.candidates_active?.length) return;
          onActiveCandidateClick(Number(candidateId));
        }
      }
    };

    fetchMatchTask();

    document.addEventListener("keydown", keyDownHandler);
    return () => {
      document.removeEventListener("keydown", keyDownHandler);
    };
  }, [
    task,
    selectedActiveCandidates,
    selectedPassiveCandidates,
    startEventTime,
    endEventTime,
    selectedEvent,
    isLoading,
    enabled,
  ]);

  useEffect(() => {
    if (!task) return;

    setStartEventTime(task.frame_start * (1 / task.fps));
    setEndEventTime(task.frame_end * (1 / task.fps));
  }, [task]);

  const clearCandidates = () => {
    setSelectedPassiveCandidates([]);
    setSelectedActiveCandidates([]);
    setAllSelectedBoxes([]);
    if (task) {
      setStartEventTime(task.frame_start * (1 / task.fps));
      setEndEventTime(task.frame_end * (1 / task.fps));
    } else {
      setStartEventTime(0);
      setEndEventTime(0);
    }
  };

  async function fetchMatchTask() {
    try {
      const res = await internApi.getMatchTask("event");
      const { id, match_number, task } = res;
      const { url } = await matchApi.getFilePath({
        matchId: match_number,
        fileType: "study_video",
      });
      setPipeConflictId(id);
      setTask(task);
      setVideoUrl(url);
    } catch (err) {
      console.log("err", err);
      toast.error("Something went wrong.");
    } finally {
      setIsLoading(false);
    }
  }

  const submitTaskResolve = async () => {
    try {
      await internApi.postMatchTask(pipeConflictId, selectedEvent, "event");

      toast.success("Result sent");

      setSelectedEvent([]);
      clearCandidates();

      await fetchMatchTask();
    } catch (error) {
      if (error.code === 409) {
        await fetchMatchTask();
      } else {
        toast(error.message);
      }
    }
  };

  const onActiveCandidateClick = (id) => {
    if (selectedActiveCandidates.includes(id)) {
      setSelectedActiveCandidates([]);
      return;
    }
    setSelectedActiveCandidates([id]);
  };

  const onPassiveCandidateClick = (id) => {
    if (selectedPassiveCandidates.includes(id)) {
      setSelectedPassiveCandidates([]);
      return;
    }
    setSelectedPassiveCandidates([id]);
  };

  const onSelectedEvent = (event) => {
    if (!task || startEventTime === null || endEventTime === null) return;

    const { question, frame_start, frame_end, extra } = task;
    const newEvent = {
      answer: [event],
      question,
      frame_start,
      frame_end,
      candidates_active: selectedActiveCandidates,
      candidates_passive: selectedPassiveCandidates,
      event_time: videoRef.current.currentTime,
      start_event_time: startEventTime,
      end_event_time: endEventTime,
      extra: extra,
    };

    setSelectedEvent([...selectedEvent, newEvent]);
    clearCandidates();
  };

  const onDeleteSelectedEvent = (event) => {
    const filteredEvent = selectedEvent.filter((item) => item.answer !== event);
    setSelectedEvent(filteredEvent);
  };

  const onDeleteSelectedCandidates = (eventTime, type, candidate) => {
    const filteredEvent = selectedEvent.map((event) => {
      if (event.event_time !== eventTime) return event;

      event[type] = event[type].filter((item) => item !== candidate);
      return event;
    });
    setSelectedEvent(filteredEvent);
  };

  const handleStartEventTime = () => {
    const currentTime = videoRef.current.currentTime;
    setStartEventTime(currentTime);
  };

  const handleEndEventTime = () => {
    const currentTime = videoRef.current.currentTime;
    setEndEventTime(currentTime);
  };

  useEffect(() => {
    setAllSelectedBoxes([
      ...selectedPassiveCandidates,
      ...selectedActiveCandidates,
    ]);
  }, [selectedActiveCandidates, selectedPassiveCandidates]);

  const onMouseDownHandler = (e) => {
    const { trackId } = e.target.attrs;
    if (!trackId || trackId === "ball") return;

    if (e.evt.button === 0) onActiveCandidateClick(Number(trackId));
    else if (e.evt.button === 2) onPassiveCandidateClick(Number(trackId));
  };

  return (
    <>
      {isLoading ? (
        <Preloader className="m-auto" />
      ) : (
        <Container className="pb-3">
          {videoUrl && task ? (
            <>
              <div className="tw-flex tw-gap-2 tw-mb-2">
                <Switch
                  label="Show boxes"
                  checked={showBoxes}
                  onChange={setShowBoxes}
                />
                <Switch
                  label="Expand"
                  checked={expanded}
                  onChange={setExpanded}
                />
              </div>
              <EventResolvingVideo
                videoRef={videoRef}
                src={videoUrl}
                task={task}
                taskType="event"
                selectedBoxes={allSelectedBoxes}
                showBoxes={showBoxes}
                expanded={expanded}
                setEnabled={setEnabled}
                onMouseDownHandler={onMouseDownHandler}
              />

              <Row className="justify-content-center mt-3">
                <Col xs={"auto"} className="d-flex gap-1">
                  <div>{pipeConflictId}</div>
                  <EventButton
                    key={"Start event time"}
                    text={"Start event time (S)"}
                    onChangeEvent={handleStartEventTime}
                    disabled={!enabled}
                  />
                  <EventButton
                    key={"End event time"}
                    text={"End event time (G)"}
                    onChangeEvent={handleEndEventTime}
                    disabled={!enabled}
                  />
                </Col>

                <Col xs={"auto"} className="d-flex gap-1">
                  <div>start event time:{startEventTime}</div>
                  <div>end event time:{endEventTime}</div>
                </Col>
              </Row>

              {task.candidates_active?.length ? (
                <Row className="justify-content-center mt-3">
                  <Col xs={"auto"}>
                    <span>Active candidates</span>
                  </Col>
                  <Col
                    xs={"auto"}
                    className="d-flex justify-content-center gap-1"
                  >
                    {task.candidates_active.map((id) => (
                      <EventButton
                        key={id}
                        text={id}
                        onChangeEvent={() => onActiveCandidateClick(id)}
                        color={
                          selectedActiveCandidates?.includes(id)
                            ? "success"
                            : "primary"
                        }
                        disabled={!enabled}
                      />
                    ))}
                  </Col>
                </Row>
              ) : null}
              {task.candidates_passive?.length ? (
                <Row className="justify-content-center mt-3">
                  <Col xs={"auto"}>
                    <span>Passive candidates</span>
                  </Col>
                  <Col
                    xs={"auto"}
                    className="d-flex justify-content-center gap-1"
                  >
                    {task.candidates_passive.map((id) => (
                      <EventButton
                        key={id}
                        text={id}
                        onChangeEvent={() => onPassiveCandidateClick(id)}
                        color={
                          selectedPassiveCandidates?.includes(id)
                            ? "success"
                            : "primary"
                        }
                        disabled={!enabled}
                      />
                    ))}
                  </Col>
                </Row>
              ) : null}
              <Row className="justify-content-center mt-3">
                <Col xs={"auto"}>
                  <span>{task.question}</span>
                </Col>
                <Col
                  xs={"auto"}
                  className="d-flex justify-content-center flex-wrap gap-1"
                >
                  {task.buttons.map((currentEvent) => (
                    <EventButton
                      key={currentEvent}
                      text={currentEvent}
                      onChangeEvent={() => onSelectedEvent(currentEvent)}
                      color="primary"
                      disabled={!enabled}
                    />
                  ))}
                </Col>
              </Row>
              {selectedEvent[0] && (
                <SelectedEvents
                  list={selectedEvent}
                  onDelete={onDeleteSelectedEvent}
                  onDeleteCandidate={onDeleteSelectedCandidates}
                />
              )}
              <Row className="justify-content-center mt-3">
                <Col
                  xs={"auto"}
                  className="d-flex justify-content-center gap-2"
                >
                  <StyledSubmit onClick={() => submitTaskResolve()}>
                    Move to next task (Enter)
                  </StyledSubmit>
                </Col>
              </Row>
            </>
          ) : null}
        </Container>
      )}
    </>
  );
};

export default EventsResolving;
